import React from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { CASHFLOW_EXPENSES_FILL, CASHFLOW_INCOME_FILL } from './cashflowColors';

export type PersonalFinancesChartTooltipItem = {
  name?: string;
  value?: number;
  payload?: {
    name?: string;
    count?: number;
    value?: number;
    incomeExpenseSplit?: boolean;
    credit?: number;
    debit?: number;
    creditCount?: number;
    debitCount?: number;
    countByYear?: Record<string, number>;
    creditByYear?: Record<string, number>;
    debitByYear?: Record<string, number>;
    creditCountByYear?: Record<string, number>;
    debitCountByYear?: Record<string, number>;
  };
};

export function chartTxCountPhrase(count: number): string {
  return `${count} TX${count === 1 ? '' : 's'}`;
}

const yearMapTotal = (map?: Record<string, number>) =>
  Object.values(map || {}).reduce((sum, amount) => sum + Number(amount || 0), 0);

export function tooltipIncomeExpenseSplit(row?: PersonalFinancesChartTooltipItem['payload']) {
  if (!row?.incomeExpenseSplit) return null;
  return {
    income: Number(row.credit || 0) || yearMapTotal(row.creditByYear),
    expenses: Number(row.debit || 0) || yearMapTotal(row.debitByYear),
    incomeCount: Number(row.creditCount || 0) || yearMapTotal(row.creditCountByYear),
    expenseCount: Number(row.debitCount || 0) || yearMapTotal(row.debitCountByYear),
    incomeByYear: row.creditByYear || {},
    expensesByYear: row.debitByYear || {},
    incomeCountByYear: row.creditCountByYear || {},
    expenseCountByYear: row.debitCountByYear || {},
  };
}

export function PersonalFinancesChartTooltip({
  active,
  payload,
  label,
  stackByYear,
  years,
  colorFor,
  formatMoney,
}: {
  active?: boolean;
  payload?: PersonalFinancesChartTooltipItem[];
  label?: string | number;
  stackByYear?: boolean;
  years?: string[];
  colorFor: (rowName: string, year?: string) => string;
  formatMoney: (value: number) => string;
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  const rowName = String(row?.name || label || '');
  const yearSet = new Set(years || []);
  const items = payload.filter((item) => {
    if (Number(item.value || 0) === 0) return false;
    if (!stackByYear) return true;
    return yearSet.size === 0 || yearSet.has(String(item.name || ''));
  });
  if (!items.length) return null;
  const totalCount =
    Number(row?.count || 0) ||
    Object.values(row?.countByYear || {}).reduce((sum, count) => sum + Number(count || 0), 0);
  const totalAmount = items.reduce((sum, item) => sum + Number(item.value || 0), 0);
  const allYearsAmount = Number(row?.value || 0) || totalAmount;
  const split = tooltipIncomeExpenseSplit(row);
  const summaryText =
    totalCount > 0
      ? `${chartTxCountPhrase(totalCount)} · ${formatMoney(Number(row?.value || totalAmount))}`
      : formatMoney(Number(row?.value || totalAmount));
  const splitAmountText = (amount: number, count: number) =>
    count > 0 ? `${chartTxCountPhrase(count)} · ${formatMoney(amount)}` : formatMoney(amount);
  const splitRow = (
    label: 'Income' | 'Expenses',
    amount: number,
    count: number,
    testId: string
  ) => (
    <Stack key={testId} direction="row" spacing={1} alignItems="center" data-testid={testId}>
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: '2px',
          bgcolor: label === 'Income' ? CASHFLOW_INCOME_FILL : CASHFLOW_EXPENSES_FILL,
          flexShrink: 0,
        }}
      />
      <Typography variant="body2">{label}</Typography>
      <Typography variant="body2" sx={{ ml: 'auto' }}>
        {splitAmountText(amount, count)}
      </Typography>
    </Stack>
  );
  return (
    <Paper
      elevation={3}
      sx={{ px: 1.5, py: 1, pointerEvents: 'none' }}
      data-testid="personal-finances-chart-tooltip"
    >
      <Typography variant="subtitle2">{rowName}</Typography>
      {!stackByYear ? (
        split ? (
          <Stack spacing={0.5}>
            {splitRow('Income', split.income, split.incomeCount, 'chart-tooltip-split-income')}
            {splitRow('Expenses', split.expenses, split.expenseCount, 'chart-tooltip-split-expenses')}
          </Stack>
        ) : (
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="chart-tooltip-line-amount"
        >
          {summaryText}
        </Typography>
        )
      ) : (
        <Stack spacing={0.5}>
        {items.map((item) => {
          const year = String(item.name || '');
          const color = colorFor(rowName, year);
          const count = Number(row?.countByYear?.[year] || 0);
          if (split) {
            const income = Number(split.incomeByYear[year] || 0);
            const expenses = Number(split.expensesByYear[year] || 0);
            const incomeCount = Number(split.incomeCountByYear[year] || 0);
            const expenseCount = Number(split.expenseCountByYear[year] || 0);
            return (
              <Stack key={`${rowName}-${year}`} spacing={0.25} sx={{ py: 0.25 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    data-testid={`chart-tooltip-swatch-${year}`}
                    data-color={color}
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '2px',
                      bgcolor: color,
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="body2" sx={{ minWidth: 44, fontWeight: 600 }}>
                    {year}
                  </Typography>
                </Stack>
                <Stack spacing={0.25} sx={{ pl: 2.5 }}>
                  {splitRow('Income', income, incomeCount, `chart-tooltip-${year}-income`)}
                  {splitRow('Expenses', expenses, expenseCount, `chart-tooltip-${year}-expenses`)}
                </Stack>
              </Stack>
            );
          }
          return (
            <Stack
              key={`${rowName}-${year}`}
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <Box
                data-testid={`chart-tooltip-swatch-${year}`}
                data-color={color}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '2px',
                  bgcolor: color,
                  flexShrink: 0,
                }}
              />
              <Typography variant="body2" sx={{ minWidth: 44 }}>
                {year}
              </Typography>
              <Typography variant="body2" sx={{ ml: 'auto' }} data-testid={`chart-tooltip-line-${year}`}>
                {count > 0
                  ? `${chartTxCountPhrase(count)} · ${formatMoney(Number(item.value || 0))}`
                  : formatMoney(Number(item.value || 0))}
              </Typography>
            </Stack>
          );
        })}
        <Stack
          spacing={split ? 0.25 : 0}
          sx={{ pt: 0.5, mt: 0.25, borderTop: 1, borderColor: 'divider' }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 10, height: 10, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ minWidth: 44, fontWeight: 700 }}>
              All years
            </Typography>
            {!split ? (
              <Typography
                variant="body2"
                sx={{ ml: 'auto', fontWeight: 700 }}
                data-testid="chart-tooltip-line-all-years"
              >
                {totalCount > 0
                  ? `${chartTxCountPhrase(totalCount)} · ${formatMoney(allYearsAmount)}`
                  : formatMoney(allYearsAmount)}
              </Typography>
            ) : null}
          </Stack>
          {split ? (
            <Stack spacing={0.25} sx={{ pl: 2.5 }}>
              {splitRow(
                'Income',
                split.income,
                split.incomeCount,
                'chart-tooltip-all-years-income'
              )}
              {splitRow(
                'Expenses',
                split.expenses,
                split.expenseCount,
                'chart-tooltip-all-years-expenses'
              )}
            </Stack>
          ) : null}
        </Stack>
        </Stack>
      )}
    </Paper>
  );
}
